import { formatCurrency, formatNumber } from './format';
import type { PricingRecalcOutput } from './hooks/usePricingRecalc';

interface KpiStripProps {
  recalc: PricingRecalcOutput;
  loCompensationDollars: number;
  netToLendwiseDollars: number;
}

/**
 * Four KPI cells per the handoff: Locked price, Net to Lendwise, LO
 * Compensation, LO Profitability. Each `.kpi[data-kind="*"]` gets a
 * top color stripe via `--stripe` (see styles.css). Secondary row uses
 * `.delta` chips.
 */
export function KpiStrip({ recalc, loCompensationDollars, netToLendwiseDollars }: KpiStripProps) {
  const { basePriceLocked, loanAmount, companyProfitDollars } = recalc;

  // Locked price: shown as mono 3-decimal value with "Adj 100.000" secondary.
  const lockedDelta = basePriceLocked - 100;
  const lockedDeltaSign = lockedDelta > 0 ? 'pos' : lockedDelta < 0 ? 'neg' : 'neu';

  // Net to Lendwise: bps = Net-to-Lendwise points × 100 (back-calc from dollars ÷ loan ÷ 0.01).
  const netBps = loanAmount > 0 ? (netToLendwiseDollars / loanAmount) * 10000 : 0;

  // LO compensation: bps = loCompDollars ÷ loanAmount × 10000.
  const loBps = loanAmount > 0 ? (loCompensationDollars / loanAmount) * 10000 : 0;

  // LO Profitability follows recalc's displayed-price precision so it
  // stays aligned with the save guard and the waterfall terminal row.
  const profitBps = loanAmount > 0 ? (companyProfitDollars / loanAmount) * 10000 : 0;

  return (
    <div className="kpi-strip" role="list" aria-label="Pricing KPIs">
      <div className="kpi" data-kind="locked" role="listitem">
        <div className="k">
          <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Locked price
        </div>
        <div className="v">{formatNumber(basePriceLocked, 3)}</div>
        <div className="secondary">
          <span>Adj 100.000</span>
          <span className={`delta ${lockedDeltaSign}`}>{lockedDelta >= 0 ? '+' : ''}{formatNumber(lockedDelta, 3)}</span>
        </div>
      </div>

      <div className="kpi" data-kind="net" role="listitem">
        <div className="k">
          <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 1 1 0 7H6" />
          </svg>
          Net to Lendwise
        </div>
        <div className="v">{formatCurrency(netToLendwiseDollars)}</div>
        <div className="secondary">
          <span className="mono tab">{formatNumber(netBps, 2)} bps</span>
          <span className="delta neu">—</span>
        </div>
      </div>

      <div className="kpi" data-kind="lo" role="listitem">
        <div className="k">
          <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          LO Compensation
        </div>
        <div className="v">{formatCurrency(loCompensationDollars)}</div>
        <div className="secondary">
          <span className="mono tab">{formatNumber(loBps, 2)} bps</span>
        </div>
      </div>

      <div className="kpi" data-kind="profit" role="listitem">
        <div className="k">
          <svg className="i" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M3 3v18h18" />
            <path d="m7 14 4-4 4 4 6-6" />
          </svg>
          LO Profitability
        </div>
        <div className="v">{formatCurrency(companyProfitDollars)}</div>
        <div className="secondary">
          <span className="mono tab">{formatNumber(profitBps, 2)} bps</span>
          <span className="delta neu">{companyProfitDollars === 0 ? 'at par' : companyProfitDollars > 0 ? 'above par' : 'below par'}</span>
        </div>
      </div>
    </div>
  );
}
